//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { searchTrackingParams } from '@apis/account-settings/accountSettingsApi';
import {
  getCurrentTrackingParams,
  getTrackingSettingsByVersion,
} from '@apis/universal-image-actions/universalImageActionsApi';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { getIsUrlTrackingParamsEnabled } from '@uia/products/selectors/getIsUrlTrackingParamsEnabled';

export function useCurrentTrackingParamsQuery(isEnabled: boolean = true) {
  const isTrackingParamsEnabled = useSelector(getIsUrlTrackingParamsEnabled);

  return useQuery({
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsProducts,
      QueryKeyConstants.UniversalImageActionsProductsTrackingParams,
      QueryKeyConstants.UniversalImageActionsProductsTrackingParamsCurrent,
    ],
    queryFn: async ({ signal }) => {
      const response = await getCurrentTrackingParams({ signal });
      return response.versionId ?? null;
    },
    enabled: isTrackingParamsEnabled && !!isEnabled,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useTrackingParamsByVersionQuery(versionId: number | undefined) {
  const isTrackingParamsEnabled = useSelector(getIsUrlTrackingParamsEnabled);

  return useQuery({
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsProducts,
      QueryKeyConstants.UniversalImageActionsProductsTrackingParams,
      QueryKeyConstants.UniversalImageActionsProductsTrackingParamsByVersion,
      versionId,
    ],
    queryFn: async ({ signal }) => {
      if (!versionId) {
        throw new Error('versionId is required');
      }
      return getTrackingSettingsByVersion({ versionId }, { signal });
    },
    enabled: isTrackingParamsEnabled && !!versionId,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useRecentTrackingParamValuesQuery(
  {
    limit = 8,
    paramName,
    searchTerm,
  }: { paramName: string; searchTerm: string; limit?: number },
  isEnabled: boolean = true,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsProducts,
      QueryKeyConstants.UniversalImageActionsProductsTrackingParams,
      QueryKeyConstants.UniversalImageActionsProductsSearchRecentTrackingParamValues,
      paramName,
      searchTerm,
      limit,
    ],
    queryFn: async ({ signal }) => {
      return searchTrackingParams(
        {
          paramName,
          searchString: searchTerm,
          findNames: false,
          limit,
        },
        { signal },
      );
    },
    enabled: !!searchTerm && isEnabled,
  });
}
